import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  Select,
  SelectItem,
  Tabs,
  Tab,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  SortDescriptor,
} from '@nextui-org/react';
import { Settlement } from 'common/src/utils/types';
import { formatMoneyUSD } from '../../utils/functions';
import LineChart from '../../../../common/src/components/charts/LineChart';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import { getWeekNumber } from 'common/src/utils/functions';
import { compareSeasonWeekKeys } from 'common/src/utils/seasonWeekOrder';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import ExcelExportButton from '../ui/ExcelExportButton';
import { UNIFIED_TABLE_CLASSNAMES } from '../../utils/table-styles';
import { exportTableToExcel } from '../../utils/export-to-excel';

interface Props {
  settlements: Settlement[];
  receiverFilter: string[];
}

const ExchangeRateEvolutionChart: React.FC<Props> = ({ settlements, receiverFilter }) => {
  const { t } = useTranslation('settlements');
  const [viewMode, setViewMode] = useState<'day' | 'week'>('week');
  const [chartOrTable, setChartOrTable] = useState<'chart' | 'table'>('chart');

  // Currencies ordered by total FOB desc, so the default selection surfaces
  // the currency with the most financial weight.
  const currencies = useMemo(() => {
    const fobByCurrency = new Map<string, number>();
    settlements.forEach((s) => {
      if (!s.local_currency) return;
      const fob = (s.settlement_details || []).reduce(
        (sum, d) => sum + (d.usd_total_fob || 0),
        0
      );
      fobByCurrency.set(
        s.local_currency,
        (fobByCurrency.get(s.local_currency) || 0) + fob
      );
    });
    return Array.from(fobByCurrency.keys()).sort(
      (a, b) => (fobByCurrency.get(b) || 0) - (fobByCurrency.get(a) || 0)
    );
  }, [settlements]);

  const [selectedCurrency, setSelectedCurrency] = useState<string>('');

  const activeCurrency = selectedCurrency || currencies[0] || '';

  const filteredSettlements = useMemo(() => {
    let filtered = settlements.filter(
      (s) => s.local_currency === activeCurrency && s.exchange_rate && s.date_sold
    );
    if (!receiverFilter.includes('all')) {
      filtered = filtered.filter((s) => receiverFilter.includes(s.receiver));
    }
    return filtered;
  }, [settlements, activeCurrency, receiverFilter]);

  const chartData = useMemo(() => {
    if (!activeCurrency || filteredSettlements.length === 0) {
      return {
        series: [],
        categories: [] as string[],
        categoryAverages: [] as (number | null)[],
      };
    }

    // Group by client, then by time period
    const byClient: Record<string, Map<string | number, { weightedSum: number; totalSales: number }>> = {};
    // Cross-client totals per period — drives the "Promedio" row (sales-
    // weighted average exchange rate across all clients in that period).
    const totalsByPeriod = new Map<
      string | number,
      { weightedSum: number; totalSales: number }
    >();

    filteredSettlements.forEach((s) => {
      const client = s.receiver || t('Sin Cliente');
      if (!byClient[client]) byClient[client] = new Map();

      const timeKey = viewMode === 'day'
        ? new Date(s.date_sold!).getTime()
        : `W${getWeekNumber(s.date_sold!)}`;

      const salesValue = (s.total_revenue_local || 0) - (s.total_commission_local || 0);
      const existing = byClient[client].get(timeKey) || { weightedSum: 0, totalSales: 0 };
      byClient[client].set(timeKey, {
        weightedSum: existing.weightedSum + (s.exchange_rate! * salesValue),
        totalSales: existing.totalSales + salesValue,
      });

      const periodTotal = totalsByPeriod.get(timeKey) || {
        weightedSum: 0,
        totalSales: 0,
      };
      totalsByPeriod.set(timeKey, {
        weightedSum: periodTotal.weightedSum + s.exchange_rate! * salesValue,
        totalSales: periodTotal.totalSales + salesValue,
      });
    });

    // Get all dates sorted
    const allDates = new Set<string | number>();
    Object.values(byClient).forEach((dateMap) => {
      dateMap.forEach((_, key) => allDates.add(key));
    });

    const sortedDates = Array.from(allDates).sort((a, b) => {
      if (viewMode === 'day') return (a as number) - (b as number);
      return compareSeasonWeekKeys(a as string, b as string);
    });

    const series = Object.entries(byClient).map(([client, dateMap], index) => ({
      name: client,
      data: sortedDates.map((timeKey) => {
        const value = dateMap.get(timeKey);
        if (!value || value.totalSales <= 0) return { x: timeKey, y: null };
        return { x: timeKey, y: value.weightedSum / value.totalSales };
      }),
      color: `hsl(${index * (360 / Object.keys(byClient).length)}, 70%, 50%)`,
    }));

    const categories = viewMode === 'day'
      ? sortedDates.map((date) => format(date as number, 'dd MMM', { locale: es }))
      : (sortedDates as string[]);

    const categoryAverages: (number | null)[] = sortedDates.map((timeKey) => {
      const v = totalsByPeriod.get(timeKey);
      if (!v || v.totalSales <= 0) return null;
      return v.weightedSum / v.totalSales;
    });

    return { series, categories, categoryAverages };
  }, [filteredSettlements, activeCurrency, viewMode]);

  // Total FOB per client (within the filtered settlements) for the default
  // series ordering. Sort applies to both chart and table views.
  const fobByClient = useMemo(() => {
    const map = new Map<string, number>();
    filteredSettlements.forEach((s) => {
      const key = s.receiver || t('Sin Cliente');
      const fob = (s.settlement_details || []).reduce(
        (sum, d) => sum + (d.usd_total_fob || 0),
        0
      );
      map.set(key, (map.get(key) || 0) + fob);
    });
    return map;
  }, [filteredSettlements, t]);

  // No active sort = default order (FOB desc). Once the user clicks a
  // header, sortDescriptor takes over. The 'client' column sorts by name;
  // any date/week column key sorts by that column's exchange-rate value.
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({});

  const sortedSeries = useMemo(() => {
    const byFobDesc = (a: { name: string }, b: { name: string }) =>
      (fobByClient.get(b.name) || 0) - (fobByClient.get(a.name) || 0);
    const arr = [...chartData.series];

    if (!sortDescriptor.column) {
      return arr.sort(byFobDesc);
    }

    const col = String(sortDescriptor.column);
    const dir = sortDescriptor.direction === 'ascending' ? 1 : -1;

    if (col === 'client') {
      return arr.sort((a, b) => dir * a.name.localeCompare(b.name));
    }

    const colIndex = chartData.categories.findIndex((c) => c === col);
    if (colIndex === -1) return arr.sort(byFobDesc);

    return arr.sort((a, b) => {
      const av = (a.data[colIndex]?.y ?? null) as number | null;
      const bv = (b.data[colIndex]?.y ?? null) as number | null;
      // Push nulls to the bottom regardless of direction.
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      return dir * (av - bv);
    });
  }, [chartData.series, chartData.categories, fobByClient, sortDescriptor]);

  const [visibleCount, setVisibleCount] = useState(10);
  // Reset pagination + sort when the user changes the currency, grouping or
  // view, since column keys change for the day/week toggle.
  useEffect(() => {
    setVisibleCount(10);
    setSortDescriptor({});
  }, [activeCurrency, viewMode, chartOrTable]);

  const visibleSeries = useMemo(
    () => sortedSeries.slice(0, visibleCount),
    [sortedSeries, visibleCount]
  );
  const hasMore = sortedSeries.length > visibleCount;

  const tooltipCustom = ({ seriesIndex, dataPointIndex, w }: any) => {
    const isDark = document.documentElement.classList.contains('dark');
    const currentX = w.globals.seriesX[seriesIndex][dataPointIndex];

    const dateDisplay = viewMode === 'day'
      ? format(new Date(currentX), 'dd MMM', { locale: es })
      : `${t('Semana')} ${w.globals.categoryLabels[dataPointIndex]}`;

    const items = w.globals.initialSeries
      .map((series: any, idx: number) => {
        const seriesX = w.globals.seriesX[idx];
        const ptIdx = seriesX.findIndex((x: any) => x === currentX);
        const yValue = ptIdx >= 0 ? series.data[ptIdx]?.y : null;
        if (yValue === null || yValue === undefined) return null;
        return { name: series.name, value: yValue, color: series.color };
      })
      .filter(Boolean)
      .sort((a: any, b: any) => b.value - a.value);

    if (items.length === 0) return '';

    const labelColor = isDark ? '#A1A1AA' : '#666';
    const valueColor = isDark ? '#ECEDEE' : '#000000';
    const bgColor = isDark ? '#1a1a2e' : '#f8f9fa';
    const titleColor = isDark ? '#ECEDEE' : '#333';

    const rows = items
      .map((item: any) => `
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;">
          <div style="display:flex;align-items:center;">
            <div style="width:8px;height:8px;border-radius:50%;background:${item.color};margin-right:8px;"></div>
            <span style="color:${labelColor};">${item.name}</span>
          </div>
          <span style="font-weight:bold;color:${valueColor};margin-left:10px;">
            ${formatMoneyUSD(item.value, { decimals: 2 })}
          </span>
        </div>
      `)
      .join('');

    return `
      <div style="background:${bgColor};border-radius:8px;box-shadow:0 2px 4px rgba(0,0,0,0.1);padding:12px;font-family:Arial,sans-serif;min-width:200px;">
        <div style="text-align:center;font-weight:bold;margin-bottom:8px;color:${titleColor};">${dateDisplay}</div>
        <div style="padding-top:8px;">${rows}</div>
      </div>
    `;
  };

  const handleExport = () => {
    // Pivoted export: clients as rows, dates/weeks as columns. Prepend a
    // PROMEDIO row showing the sales-weighted average per period across
    // every client (same data shown on screen).
    exportTableToExcel({
      fileName: 'Evolutivo_Tipos_de_Cambio',
      sheetName: t('Evolutivo'),
      headers: [t('CLIENTE'), ...chartData.categories],
      rows: [
        [
          t('PROMEDIO'),
          ...chartData.categoryAverages.map((avg) =>
            avg !== null ? formatMoneyUSD(avg, { decimals: 2 }) : '—'
          ),
        ],
        ...sortedSeries.map((serie) => [
          serie.name,
          ...chartData.categories.map((_, index) =>
            serie.data[index]?.y !== null && serie.data[index]?.y !== undefined
              ? formatMoneyUSD(serie.data[index].y as number, { decimals: 2 })
              : '—'
          ),
        ]),
      ],
    });
  };

  if (currencies.length === 0) return null;

  return (
    <div>
      <div className='flex items-center justify-between mb-3'>
        <h3 className='text-xl font-semibold'>{t('Evolutivo')}</h3>
        <div className='flex items-center gap-2'>
          <Select
            label={t('Moneda')}
            selectedKeys={activeCurrency ? [activeCurrency] : []}
            onChange={(e) => setSelectedCurrency(e.target.value)}
            className='w-36'
            size='sm'
          >
            {currencies.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </Select>
          <Tabs
            aria-label={t('Agrupación')}
            selectedKey={viewMode}
            onSelectionChange={(key) => setViewMode(key as 'day' | 'week')}
            size='sm'
            variant='bordered'
          >
            <Tab key='day' title={t('Día')} />
            <Tab key='week' title={t('Semana')} />
          </Tabs>
          <Tabs
            aria-label={t('Vista')}
            selectedKey={chartOrTable}
            onSelectionChange={(key) => setChartOrTable(key as 'chart' | 'table')}
            size='sm'
            variant='bordered'
          >
            <Tab key='table' title={t('Tabla')} />
            <Tab key='chart' title={t('Gráfico')} />
          </Tabs>
          <ExcelExportButton onClick={handleExport} />
        </div>
      </div>
      <Card className='w-full'>
        <CardBody className={chartOrTable === 'chart' ? '' : 'p-0'}>
          {chartData.series.length > 0 ? (
            chartOrTable === 'chart' ? (
              <LineChart
                key={`tc-${activeCurrency}-${viewMode}`}
                series={visibleSeries}
                xaxisCategories={chartData.categories}
                xaxisTitle={viewMode === 'day' ? t('Día (Fecha de Venta)') : t('Semana (Fecha de Venta)')}
                yaxisTitle={`${t('Tipo de Cambio')} (${activeCurrency})`}
                tooltipCustom={tooltipCustom}
                selectedCurrency={activeCurrency}
                priceView='kg'
                height={500}
                legendPosition='bottom'
                viewMode={viewMode}
                minYAxisValue={
                  Math.min(
                    ...visibleSeries.map((serie) =>
                      Math.min(...serie.data.filter((p) => p.y !== null).map((p) => p.y as number))
                    )
                  ) || 0
                }
              />
            ) : (
              <Table
                // Force a remount when viewMode changes so the column set
                // swaps cleanly — NextUI's collection otherwise appends
                // the new columns instead of replacing them.
                key={`tc-table-${activeCurrency}-${viewMode}`}
                isHeaderSticky
                sortDescriptor={sortDescriptor}
                onSortChange={setSortDescriptor}
                classNames={{
                  ...UNIFIED_TABLE_CLASSNAMES,
                  base: 'max-h-[600px] overflow-auto',
                }}
                aria-label={t('Evolutivo Tipos de Cambio')}
              >
                <TableHeader>
                  <TableColumn key='client' allowsSorting>
                    {t('CLIENTE')}
                  </TableColumn>
                  {chartData.categories.map((category) => (
                    <TableColumn key={category} allowsSorting>
                      {category}
                    </TableColumn>
                  ))}
                </TableHeader>
                <TableBody>
                  <TableRow
                    key='__average__'
                    className='bg-default-100 dark:bg-default-50 font-semibold border-b-2 border-default-200 dark:border-default-100'
                  >
                    <TableCell className='font-bold'>
                      {t('PROMEDIO')}
                    </TableCell>
                    {chartData.categoryAverages.map((avg, i) => (
                      <TableCell
                        key={`avg-${i}`}
                        className='font-semibold tabular-nums'
                      >
                        {avg !== null
                          ? formatMoneyUSD(avg, { decimals: 2 })
                          : '—'}
                      </TableCell>
                    ))}
                  </TableRow>
                  {visibleSeries.map((serie) => (
                    <TableRow key={serie.name}>
                      <TableCell className='font-medium'>{serie.name}</TableCell>
                      {chartData.categories.map((category, index) => {
                        const value = serie.data[index]?.y;
                        if (value === null || value === undefined) {
                          return (
                            <TableCell key={`${serie.name}-${category}`}>
                              —
                            </TableCell>
                          );
                        }
                        const avg = chartData.categoryAverages[index];
                        const formatted = formatMoneyUSD(value as number, {
                          decimals: 2,
                        });
                        // Without a period average we can't compare; render
                        // plain. Otherwise classify as above/below using a
                        // tiny epsilon to ignore float-noise.
                        if (avg === null || avg === undefined) {
                          return (
                            <TableCell key={`${serie.name}-${category}`}>
                              {formatted}
                            </TableCell>
                          );
                        }
                        const diff = (value as number) - avg;
                        const eps = 0.0001;
                        const isAbove = diff > eps;
                        const isBelow = diff < -eps;
                        const colorClass = isAbove
                          ? 'text-green-600 dark:text-green-400'
                          : isBelow
                            ? 'text-red-600 dark:text-red-400'
                            : '';
                        const glyph = isAbove ? '▲' : isBelow ? '▼' : '';
                        const sign = diff > 0 ? '+' : '';
                        const tooltip = `${sign}${diff.toFixed(4)} vs ${t('promedio')}`;
                        return (
                          <TableCell key={`${serie.name}-${category}`}>
                            <span
                              className={`inline-flex items-center gap-1 ${colorClass}`}
                              title={tooltip}
                            >
                              <span>{formatted}</span>
                              {glyph && (
                                <span className='text-[10px] leading-none'>
                                  {glyph}
                                </span>
                              )}
                            </span>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          ) : (
            <p className='text-default-400 text-sm text-center py-8'>
              {t('No hay datos para la moneda seleccionada')}
            </p>
          )}
          {hasMore && chartData.series.length > 0 && (
            <div className='flex justify-center mt-2'>
              <Button
                variant='light'
                size='sm'
                onPress={() =>
                  setVisibleCount((c) =>
                    Math.min(c + 10, sortedSeries.length)
                  )
                }
                className='w-full'
              >
                {t('Cargar más')} ({sortedSeries.length - visibleCount}{' '}
                {t('restantes')})
              </Button>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default React.memo(ExchangeRateEvolutionChart);
